/**
 * LiveKit data-channel payload for trainer → student session clock sync.
 * Students use this so Expected end updates on extend / Q&A pause.
 *
 * Flow:
 *   step 1) encodeLiveSessionClock on trainer publish
 *   step 2) decodeLiveSessionClock on student receive
 */

import { parseEndTimeToMinutes } from "./topicProgress/sessionEndTime";

export const LIVE_SESSION_CLOCK_TYPE = "codelive.session-clock" as const;

export type LiveSessionClockMessage = {
  type: typeof LIVE_SESSION_CLOCK_TYPE;
  v: 1;
  projectedEndTime: string;
  qaPaused: boolean;
  topicTimeBonusSec: number;
  /** Finished + in-progress pause seconds that shift expected end. */
  pauseAccumulatedSec: number;
  completedTopicIds: string[];
  /** Topics the trainer marked as Skip (subset of completedTopicIds). */
  skippedTopicIds: string[];
  /** Completed topic/subtopic id → actual elapsed seconds (for Time taken). */
  topicActualDurationsSec: Record<string, number>;
  topicElapsedSec: number;
  /** Elapsed seconds on the current nested subtopic (0 when none). */
  subtopicElapsedSec?: number;
  sessionElapsedSec: number;
  /** Trainer has started the current topic clock. */
  topicStarted: boolean;
  /** Current parent topic id (trainer clock). */
  currentTopicId?: string | null;
  /** Current nested subtopic id when teaching a subtopic. */
  currentSubtopicId?: string | null;
  /**
   * Wall-clock ISO when the current subtopic started (trainer source of truth).
   * Students use this so Time taken continues from start, not a reset counter.
   */
  currentSubtopicActualStartAt?: string | null;
};

export function encodeLiveSessionClock(
  msg: Omit<LiveSessionClockMessage, "type" | "v">,
): Uint8Array {
  // ---------------------------------------------------------------
  // step 1: Wrap fields with type/v and encode for publishData
  // ---------------------------------------------------------------
  const payload: LiveSessionClockMessage = {
    type: LIVE_SESSION_CLOCK_TYPE,
    v: 1,
    ...msg,
  };
  return new TextEncoder().encode(JSON.stringify(payload));
}

function coerceDurationMap(
  raw: unknown,
): Record<string, number> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, number> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    const key = String(id || "").trim();
    const sec = Math.max(0, Math.floor(Number(value) || 0));
    if (key) out[key] = sec;
  }
  return out;
}

export function decodeLiveSessionClock(
  data: Uint8Array,
): LiveSessionClockMessage | null {
  // ---------------------------------------------------------------
  // step 1: Parse bytes and validate type/v
  // ---------------------------------------------------------------
  try {
    const raw = JSON.parse(new TextDecoder().decode(data)) as Partial<
      LiveSessionClockMessage
    > & { topicActualDurationsSec?: unknown };
    if (raw?.type !== LIVE_SESSION_CLOCK_TYPE || raw.v !== 1) return null;
    if (typeof raw.projectedEndTime !== "string") return null;

    // ---------------------------------------------------------------
    // step 2: Coerce numeric / array fields safely
    // ---------------------------------------------------------------
    return {
      type: LIVE_SESSION_CLOCK_TYPE,
      v: 1,
      projectedEndTime: raw.projectedEndTime,
      qaPaused: Boolean(raw.qaPaused),
      topicTimeBonusSec: Math.max(0, Number(raw.topicTimeBonusSec) || 0),
      pauseAccumulatedSec: Math.max(0, Number(raw.pauseAccumulatedSec) || 0),
      completedTopicIds: Array.isArray(raw.completedTopicIds)
        ? raw.completedTopicIds.map(String)
        : [],
      skippedTopicIds: Array.isArray(raw.skippedTopicIds)
        ? raw.skippedTopicIds.map(String)
        : [],
      topicActualDurationsSec: coerceDurationMap(raw.topicActualDurationsSec),
      topicElapsedSec: Math.max(0, Number(raw.topicElapsedSec) || 0),
      subtopicElapsedSec: Math.max(0, Number(raw.subtopicElapsedSec) || 0),
      sessionElapsedSec: Math.max(0, Number(raw.sessionElapsedSec) || 0),
      topicStarted: Boolean(raw.topicStarted),
      currentTopicId: raw.currentTopicId ? String(raw.currentTopicId) : null,
      currentSubtopicId: raw.currentSubtopicId
        ? String(raw.currentSubtopicId)
        : null,
      currentSubtopicActualStartAt:
        typeof raw.currentSubtopicActualStartAt === "string" &&
        raw.currentSubtopicActualStartAt.trim()
          ? raw.currentSubtopicActualStartAt.trim()
          : null,
    };
  } catch {
    return null;
  }
}

function clockMinutesDiff(prevM: number, nextM: number): number {
  let diff = nextM - prevM;
  if (diff > 12 * 60) diff -= 24 * 60;
  if (diff < -12 * 60) diff += 24 * 60;
  return diff;
}

/** Ignore small Expected-end jitter while the same topic/subtopic is active. */
export function stabilizeProjectedEndTime(
  prev: string | undefined,
  next: string,
  reason: {
    topicChanged: boolean;
    subChanged: boolean;
    bonusChanged: boolean;
    qaPausedChanged: boolean;
    completedCountChanged: boolean;
  },
): string {
  const trimmed = String(next || "").trim();
  if (!trimmed) return String(prev || "").trim() || next;
  const prevTrim = String(prev || "").trim();
  if (!prevTrim) return trimmed;
  if (
    reason.topicChanged ||
    reason.subChanged ||
    reason.bonusChanged ||
    reason.qaPausedChanged ||
    reason.completedCountChanged
  ) {
    return trimmed;
  }
  const prevM = parseEndTimeToMinutes(prevTrim);
  const nextM = parseEndTimeToMinutes(trimmed);
  if (prevM == null || nextM == null) return trimmed;
  if (Math.abs(clockMinutesDiff(prevM, nextM)) <= 3) return prevTrim;
  return trimmed;
}

/** Ignore stale clock packets that briefly clear Q&A pause. */
export function stabilizeQaPaused(
  prevPaused: boolean,
  msg: Pick<
    LiveSessionClockMessage,
    "qaPaused" | "sessionElapsedSec" | "pauseAccumulatedSec"
  >,
  lastApplied: {
    sessionElapsed: number;
    pauseAccumulated: number;
  },
): boolean {
  if (msg.qaPaused) return true;
  if (!prevPaused) return false;
  const nextSess = Math.max(0, msg.sessionElapsedSec || 0);
  const nextPause = Math.max(0, msg.pauseAccumulatedSec || 0);
  if (nextSess < lastApplied.sessionElapsed) return true;
  if (
    nextSess === lastApplied.sessionElapsed &&
    nextPause < lastApplied.pauseAccumulated
  ) {
    return true;
  }
  return false;
}
